/**
 * PortalSwitcher — "Switch portal" section shared by every portal's account
 * menu (the admin shell + the PMS dashboard today). A portal is a presentational
 * domain noun reused in 2+ places, so it lives in `entities/` per Hybrid Z
 * (ADR frontend-0001) — one source of truth instead of a per-portal copy.
 *
 * **Product divergence from the artboard.** `shell.jsx` deliberately ships NO
 * portal toggle ("you only see the portal you're in"). The product owner
 * (Miguel, 2026-05-27) asked for an in-menu switcher for multi-portal users,
 * consistent across every portal's menu; recorded in
 * `docs/decisions/scrum-114-admin-portal-switcher-in-menu.md`.
 *
 * **Visibility.** Renders when there are >= 2 portals to offer. The roster is
 * `me.portals` (a required array shipped by `/auth/me` — SCRUM-131) intersected
 * with the known portal order; the explicit `[]` "known zero" case hides the
 * switcher, as does any single-portal roster.
 *
 * **Destinations.** PMS's home is the dashboard root `/` today (no dedicated
 * `/pms` route); the MSA portal isn't built yet, so it renders disabled
 * ("Coming soon") rather than dead-linking. The current portal (passed by the
 * host menu — admin → 'Admin', dashboard → 'PMS') is marked and non-navigating.
 * `PORTAL_META` is the single source of truth — move a route here if a portal
 * relocates.
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@sparta/ui-kit';
import type { MePortalId } from '../../shared/auth/use-me.js';

interface PortalMeta {
  readonly label: string;
  /** Destination path, or `null` when the portal isn't built yet (disabled). */
  readonly route: string | null;
}

const PORTAL_META: Readonly<Record<MePortalId, PortalMeta>> = {
  PMS: { label: 'PMS', route: '/' },
  MSA: { label: 'MSA', route: null },
  Admin: { label: 'Admin', route: '/admin' },
};

/** Deterministic render order (independent of the order `portals[]` arrives). */
const PORTAL_ORDER: readonly MePortalId[] = ['PMS', 'MSA', 'Admin'];

/**
 * The roster to display: the user's real entitlements (`portals`) intersected
 * with the known portal order, so render order is deterministic regardless of
 * the order entitlements arrive. The explicit `[]` "known zero" case yields an
 * empty roster, which hides the switcher.
 */
function portalRoster(
  portals: ReadonlyArray<MePortalId>,
): readonly MePortalId[] {
  return PORTAL_ORDER.filter((id) => portals.includes(id));
}

/**
 * True when there are >= 2 portals to offer (so the switcher should render).
 * Exported so a host account menu can decide whether to render the surrounding
 * divider without duplicating this rule.
 */
export function hasPortalSwitch(
  portals: ReadonlyArray<MePortalId>,
): boolean {
  return portalRoster(portals).length >= 2;
}

export interface PortalSwitcherProps {
  readonly portals: ReadonlyArray<MePortalId>;
  /** The portal the user is currently in (admin shell → 'Admin', PMS → 'PMS'). */
  readonly current: MePortalId;
}

export function PortalSwitcher({
  portals,
  current,
}: PortalSwitcherProps): React.ReactElement | null {
  const roster = portalRoster(portals);
  if (roster.length < 2) return null;

  return (
    <div className="px-2 py-1">
      <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[var(--fg-tertiary)]">
        Switch portal
      </div>
      <ul role="list" className="m-0 flex list-none flex-col gap-0.5 p-0">
        {roster.map((id) => (
          <li key={id}>
            <PortalRow meta={PORTAL_META[id]} current={id === current} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function PortalRow({
  meta,
  current,
}: {
  readonly meta: PortalMeta;
  readonly current: boolean;
}): React.ReactElement {
  const base = 'flex items-center gap-3 py-2.5 px-2 rounded-md text-sm';

  if (current) {
    return (
      <div className={`${base} text-[var(--fg-primary)]`} aria-current="true">
        <Icon name="portal" size={16} aria-hidden />
        <span className="flex-1">{meta.label}</span>
        <span className="inline-flex items-center gap-1 text-[11px] text-[var(--fg-tertiary)]">
          <Icon name="check" size={13} aria-hidden />
          Current
        </span>
      </div>
    );
  }

  if (meta.route === null) {
    return (
      <div
        className={`${base} text-[var(--fg-disabled)] cursor-not-allowed`}
        aria-disabled="true"
      >
        <Icon name="portal" size={16} aria-hidden />
        <span className="flex-1">{meta.label}</span>
        <span className="text-[11px]">Coming soon</span>
      </div>
    );
  }

  return (
    <Link
      to={meta.route}
      className={`${base} no-underline text-[var(--fg-primary)] hover:bg-[var(--bg-hover)]`}
    >
      <Icon name="portal" size={16} aria-hidden />
      <span className="flex-1">{meta.label}</span>
      <Icon name="chevRight" size={14} aria-hidden />
    </Link>
  );
}
